import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Signal, SignalDetail } from '../../../../types/signals-live';
import { getSignalById, reclassifySignal, listSimulations, deleteSimulation, createSimulation } from '../../../../utils/api';
import { useWebSocket } from '../../../../contexts/WebSocketContext';
import SignalDetailPanel from './SignalDetailPanel';
import { TaxonomyLabelReview } from '../../signals-live/TaxonomyLabelReview';
import BreadcrumbNav from '../../shared/BreadcrumbNav';
import SimulationsTableByDay from '../../ombudsman/SimulationsTableByDay';
import { Simulation } from '../../ombudsman/SimulationCard';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { isOmbudsman } from '../../../../utils/rbac';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import IconWrapper from '../../../IconWrapper';
import { FiArrowLeft, FiBarChart2, FiPlay } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useNotice } from '../../shared/NoticeProvider';

const SignalDetailPage: React.FC = () => {
  const { show: showNotice } = useNotice();
  const { signalId } = useParams<{ signalId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getBackPath, navigateWithReferrer } = useNavigation();
  const { user } = useAuth();
  const [signal, setSignal] = useState<Signal | null>(null);
  const [detail, setDetail] = useState<SignalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progressTick, setProgressTick] = useState(0);
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null);
  const [showSimulations, setShowSimulations] = useState(false);
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [isLoadingSimulations, setIsLoadingSimulations] = useState(false);
  const [isCreatingSimulation, setIsCreatingSimulation] = useState(false);

  const { subscribe, unsubscribe, addMessageListener } = useWebSocket();

  const fetchSignal = useCallback(async () => {
    if (!signalId) return;

    try {
      setLoading(true);
      setError(null);
      
      const data = await getSignalById(signalId);
      setSignal(data);
      setDetail(data);
    } catch (err) {
      console.error('Failed to fetch signal:', err);
      setError(err instanceof Error ? err.message : 'Failed to load signal');
    } finally {
      setLoading(false);
    }
  }, [signalId]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    if (!signalId) return;

    fetchSignal();
    
    // Subscribe to signal updates
    subscribe('signal', signalId);

    const handleMessage = (data: any) => {
      if (data.type === 'signal.updated' || data.type === 'signal.classified') {
        if (data.signal_id === signalId) {
          if (data.type === 'signal.classified') {
            // Refetch complete signal for classification updates
            fetchSignal();
          } else {
            // Merge other updates
            setSignal((prev) => prev ? { ...prev, ...data.data } : null);
            setDetail((prev) => prev ? { ...prev, ...data.data } : null);
          }
        }
      }
    };

    const cleanup = addMessageListener(handleMessage);

    return () => {
      unsubscribe('signal', signalId);
      cleanup();
    };
  }, [signalId, fetchSignal, subscribe, unsubscribe, addMessageListener]);

  // Progress tick animation
  useEffect(() => {
    const interval = setInterval(() => {
      setProgressTick((prev) => prev + 1);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const handleReclassifySignal = async (id: string) => {
    try {
      // Announced here rather than in the menu holding the control, which
      // closes on the selecting click before anything in it can be read.
      const { status } = await reclassifySignal(id);
      if (status === 'already_classifying' || status === 'import_pending') {
        // Through `NoticeProvider` and not this file's `react-toastify` import:
        // nothing in the app mounts a `ToastContainer`, so every `toast.*` call
        // on this page renders nothing.
        showNotice({
          type: 'info',
          message:
            status === 'already_classifying'
              ? 'This signal is already being classified.'
              : 'This signal is waiting for its import to finish.',
          autoDismissMs: 5000,
        });
      }
      // Returned as well as shown: the menu holding this control keeps its own
      // success tick, and would otherwise announce a run that was refused to
      // anyone who reopened it within three seconds.
      return status;
    } catch (error) {
      console.error('Failed to reclassify signal:', error);
      throw error;
    }
  };

  const handleLabelTap = (meta: any) => {
    // Navigate to taxonomy view if needed
    if (meta.dimension_id && meta.category_id) {
      navigate(`/v3/taxonomy?label=${encodeURIComponent(meta.label_name)}`);
    }
  };

  const handleOpenFeedback = async (id: string) => {
    // Ensure detail is loaded before opening feedback
    if (!detail && signalId === id) {
      await fetchSignal();
    }
    setFeedbackFor(id);
  };

  // Get referrer source for breadcrumb
  const getReferrerSource = (): string => {
    const stateReferrer = (location.state as any)?.referrer;
    if (stateReferrer) {
      if (stateReferrer.includes('/ombudsman')) {
        return 'Case Guidance';
      }
      if (stateReferrer.includes('/vault')) {
        return 'Conduct Records';
      }
      if (stateReferrer.includes('/today')) {
        return 'Today';
      }
      if (stateReferrer.includes('/signals')) {
        return 'Signals';
      }
    }
    // Default fallback
    return 'Conduct Records';
  };

  const handleBack = () => {
    const backPath = getBackPath();
    navigate(backPath);
  };

  const handleViewSimulations = () => {
    if (showSimulations) {
      setShowSimulations(false);
    } else {
      setShowSimulations(true);
      // Scroll to simulations panel
      setTimeout(() => {
        const panel = document.getElementById('simulations-panel');
        if (panel) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleViewSimulationsInWorkspace = () => {
    if (signalId) {
      navigateWithReferrer(`/v3/ombudsman?signal_id=${signalId}`, window.location.pathname);
    }
  };

  // Fetch simulations for this signal
  const fetchSimulations = useCallback(async () => {
    if (!signalId) return;

    try {
      setIsLoadingSimulations(true);
      const data = await listSimulations(signalId);
      const simulationsArray = Array.isArray(data) ? data : (data?.simulations || data?.data || []);
      setSimulations(simulationsArray);
    } catch (error) {
      console.error('Failed to fetch simulations:', error);
      toast.error('Failed to load simulations');
    } finally {
      setIsLoadingSimulations(false);
    }
  }, [signalId]);

  const handleDeleteSimulation = async (simulationId: string) => {
    try {
      await deleteSimulation(simulationId);
      setSimulations(prev => prev.filter(s => s.id !== simulationId));
      toast.success('Case deleted successfully');
    } catch (error) {
      console.error('Failed to delete case:', error);
      toast.error('Failed to delete case');
      throw error;
    }
  };

  const handleStartSimulation = async () => {
    if (!signalId) return;

    try {
      setIsCreatingSimulation(true);
      const response = await createSimulation(signalId);
      if (response.id) {
        toast.success('Case guidance started');
        navigateWithReferrer(`/v3/ombudsman/simulations/${response.id}`, location.pathname);
      } else {
        throw new Error('Case guidance creation failed');
      }
    } catch (error) {
      console.error('Failed to create case guidance:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start case guidance');
    } finally {
      setIsCreatingSimulation(false);
    }
  };

  const handleSimulationRowClick = (simulationId: string) => {
    navigateWithReferrer(`/v3/ombudsman/simulations/${simulationId}`, location.pathname);
  };

  const handleViewSignal = (signalId: string) => {
    // We're already viewing this signal, so scroll to top or refresh
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isOmbudsmanUser = isOmbudsman(user);
  
  // Fetch simulations when signalId is available
  useEffect(() => {
    if (isOmbudsmanUser && signalId) {
      fetchSimulations();
    }
  }, [isOmbudsmanUser, signalId, fetchSimulations]);

  const signalTitle = signal?.summary || detail?.summary || signal?.verbatim?.substring(0, 50) || 'Signal';
  const breadcrumbItems = [
    {
      label: getReferrerSource(),
      path: getBackPath(),
    },
    {
      label: `Signal: ${signalTitle.length > 40 ? signalTitle.substring(0, 40) + '...' : signalTitle}`,
    },
  ];

  const quickActions = isOmbudsmanUser ? (
    <Button
      variant="outline"
      size="sm"
      onClick={handleViewSimulationsInWorkspace}
      className="flex items-center gap-2"
    >
      <IconWrapper icon={FiBarChart2} className="h-4 w-4" />
      View Simulations
    </Button>
  ) : null;

  if (loading) {
    return (
      <div className="flex h-full bg-[#fbf9f6] items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4342FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading signal details...</p>
        </div>
      </div>
    );
  }

  if (error || !signal) {
    return (
      <div className="flex h-full bg-[#fbf9f6] items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading signal</h3>
          <p className="text-gray-600 mb-4">{error || 'Signal not found'}</p>
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#4342FF] text-white rounded-lg hover:bg-[#3332DD] transition-colors"
          >
            <IconWrapper icon={FiArrowLeft} className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbf9f6]">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav
        items={breadcrumbItems}
        quickActions={quickActions}
        onBack={handleBack}
      />

      <div className="w-full px-6 py-6">
        {/* Signal Detail Panel */}
        <div className="max-w-full">
          <SignalDetailPanel
            signal={signal}
            detail={detail || undefined}
            signalId={signalId}
            progressTick={progressTick}
            onReclassify={handleReclassifySignal}
            onLabelTap={handleLabelTap}
            onOpenFeedback={handleOpenFeedback}
          />
        </div>

        {/* Simulations Table (if ombudsman user) */}
        {isOmbudsmanUser && signalId && (
          <div id="simulations-panel" className="mt-6 max-w-full">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Past Outcome Simulations</CardTitle>
                  <Button
                    size="sm"
                    onClick={handleStartSimulation}
                    disabled={isCreatingSimulation}
                    className="flex items-center gap-2"
                  >
                    <IconWrapper icon={FiPlay} className="h-4 w-4" />
                    {isCreatingSimulation ? 'Starting...' : 'New Case'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <SimulationsTableByDay
                  simulations={simulations}
                  onViewSignal={handleViewSignal}
                  onDelete={handleDeleteSimulation}
                  onRowClick={handleSimulationRowClick}
                  isLoading={isLoadingSimulations}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {feedbackFor && detail?.classification?.id && detail?.taxonomy_classification && (
        <TaxonomyLabelReview
          classification_id={detail.classification.id}
          taxonomy_classification={detail.taxonomy_classification}
          onClose={() => setFeedbackFor(null)}
          signal_id={detail.id}
          signalSummary={detail.summary_detailed || detail.summary}
          signalVerbatim={detail.verbatim}
          customerName={detail.customer_name || detail.customer?.name}
          conductScore={detail.conduct_score ?? detail.classification?.conduct_score}
        />
      )}
    </div>
  );
};

export default SignalDetailPage;

